import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Button, Form, Row, Col, Alert, Container } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { CenteredLoader, InlineLoader } from '../../components/LoadingSpinner';
import { api } from '../../shared/api';
import Select from '../../components/form/AppSelect';
import AsyncCreatableSelect from '../../components/form/AppAsyncCreatableSelect';
import '../../shared/ReactSelectDarkMode.css';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../../shared/CurrencyContext';
import { useQuery, useMutation } from '@tanstack/react-query';

// Custom theme for React Select to support dark mode
const selectTheme = theme => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary: 'var(--tblr-primary)',
    primary75: 'var(--tblr-primary-rgb)',
    primary50: 'var(--tblr-primary-rgb)',
    primary25: 'var(--tblr-primary-rgb)',
    danger: 'var(--tblr-danger)',
    dangerLight: 'var(--tblr-danger-rgb)',
    neutral0: 'var(--tblr-bg-forms)',
    neutral5: 'var(--tblr-bg-surface)',
    neutral10: 'var(--tblr-bg-surface)',
    neutral20: 'var(--tblr-border-color)',
    neutral30: 'var(--tblr-border-color)',
    neutral40: 'var(--tblr-body-color-secondary)',
    neutral50: 'var(--tblr-body-color-secondary)',
    neutral60: 'var(--tblr-body-color-secondary)',
    neutral70: 'var(--tblr-body-color-secondary)',
    neutral80: 'var(--tblr-body-color)',
    neutral90: 'var(--tblr-body-color)'
  }
});

// Debounce function to limit API calls during typing
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const ProjectEdit = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { currencies } = useCurrency();
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedCustomerOption, setSelectedCustomerOption] = useState(null);
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    location: '',
    status: '',
    type: '',
    budget: '',
    customer: '',
    currency: '',
    processed_by: null,
    tax_rate: '21.00',
    version: '1.0',
    locked: false,
    discount: 0,
    billed_days: 1
  });

  const { isLoading: loading } = useQuery({
    queryKey: ['project-edit', projectId],
    queryFn: async () => {
      const projectResponse = await api.get(`projects/api/projects/${projectId}/`);
      const { status, type, customer, currency, processed_by, ...restProject } = projectResponse;
      const formattedProject = {
        ...restProject,
        start_date: projectResponse.start_date ? projectResponse.start_date.split('T')[0] : '',
        end_date: projectResponse.end_date ? projectResponse.end_date.split('T')[0] : '',
        status: status?.id || status || '',
        type: type?.id || type || '',
        customer: customer?.id || customer || '',
        currency: currency?.id || currency || '',
        processed_by: processed_by?.id || processed_by || null
      };

      if (!formattedProject.tax_rate || parseFloat(formattedProject.tax_rate) === 0) {
        try {
          const defaultTax = await api.get('settings/api/tax/default/');
          const rate = defaultTax?.rate || defaultTax?.data?.rate;
          if (rate) formattedProject.tax_rate = String(rate);
        } catch { /* no default configured, keep as is */ }
      }

      setFormData(formattedProject);

      if (customer) {
        setSelectedCustomerOption({
          value: customer?.id || customer,
          label: customer?.company_name || customer?.contact_name || `Customer #${customer?.id || customer}`
        });
      }

      const [statusesResponse, typesResponse, crewResponse] = await Promise.all([
        api.get('projects/api/project-statuses/'),
        api.get('projects/api/project-types/'),
        api.get('crew/api/crew/')
      ]);

      return {
        statuses: statusesResponse,
        types: typesResponse,
        crew: Array.isArray(crewResponse) ? crewResponse : (crewResponse.results || []),
      };
    },
  });

  // Keep these state vars for live search (setStatusOptions / setTypeOptions used in debouncers)
  const [statusOptions, setStatusOptions] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const { data: initData } = useQuery({ queryKey: ['project-edit', projectId] }); // read cached data

  const crewMemberOptions = useMemo(
    () => (initData?.crew || []).map(cm => ({
      value: cm.id,
      label: `${cm.company?.company_name || ''} | ${cm.display_name || ''}`
    })),
    [initData]
  );

  const currencyOptions = useMemo(
    () => (currencies || []).map(curr => ({ value: curr.id, label: `${curr.code} - ${curr.name}` })),
    [currencies]
  );

  // Populate status/type options from initial query data
  useEffect(() => {
    if (initData?.statuses) setStatusOptions(initData.statuses.map(s => ({ value: s.id, label: s.name })));
    if (initData?.types) setTypeOptions(initData.types.map(t => ({ value: t.id, label: t.name })));
  }, [initData]);

  // Functions to fetch with search term (live search)
  const fetchProjectStatuses = async (searchTerm = '') => {
    try {
      let url = 'projects/api/project-statuses/';
      if (searchTerm) url += `?search=${encodeURIComponent(searchTerm)}`;
      const response = await api.get(url);
      setStatusOptions(response.map(status => ({ value: status.id, label: status.name })));
    } catch (err) {
      console.error('Error fetching project statuses:', err);
    }
  };

  const fetchProjectTypes = async (searchTerm = '') => {
    try {
      let url = 'projects/api/project-types/';
      if (searchTerm) url += `?search=${encodeURIComponent(searchTerm)}`;
      const response = await api.get(url);
      setTypeOptions(response.map(type => ({ value: type.id, label: type.name })));
    } catch (err) {
      console.error('Error fetching project types:', err);
    }
  };

  // Debounced search functions
  const loadCustomerOptions = async (inputValue) => {
    try {
      const url = inputValue
        ? `customers/api/customers/?page_size=20&search=${encodeURIComponent(inputValue)}`
        : 'customers/api/customers/?page_size=20';
      const response = await api.get(url);
      return response.map(c => ({
        value: c.id,
        label: c.company_name || c.contact_name || `Customer #${c.id}`
      }));
    } catch (err) {
      console.error('Error fetching customers:', err);
      return [];
    }
  };

  const handleCreateCustomer = async (inputValue) => {
    try {
      setCreatingCustomer(true);
      const response = await api.post('customers/api/customers/', { company_name: inputValue });
      const newOption = {
        value: response.id,
        label: response.company_name || inputValue
      };
      setSelectedCustomerOption(newOption);
      setFormData(prev => ({ ...prev, customer: response.id }));
    } catch (err) {
      console.error('Error creating customer:', err);
      setError(t('customers.failedToCreate', 'Failed to create customer'));
    } finally {
      setCreatingCustomer(false);
    }
  };

  const debouncedStatusSearch = useCallback(
    debounce((inputValue) => {
      fetchProjectStatuses(inputValue);
    }, 300),
    []
  );

  const debouncedTypeSearch = useCallback(
    debounce((inputValue) => {
      fetchProjectTypes(inputValue);
    }, 300),
    []
  );

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    // Handle checkboxes separately
    if (type === 'checkbox') {
      setFormData({ ...formData, [name]: checked });
      return;
    }

    // Convert empty string to null for number fields
    const processedValue = (type === 'number' && value === '') ? null : value;
    setFormData({
      ...formData,
      [name]: processedValue
    });
  };

  const handleSelectChange = (name) => (selectedOption) => {
    setFormData({
      ...formData,
      [name]: selectedOption ? selectedOption.value : ''
    });
  };

  const saveMutation = useMutation({
    mutationFn: (submitData) => api.put(`projects/api/projects/${projectId}/`, submitData),
    onSuccess: () => {
      setSuccess(t('projects.updateSuccess'));
      setTimeout(() => setSuccess(null), 3000);
    },
    onError: (err) => {
      console.error('Error updating project:', err);
      setError(t('projects.failedToUpdateProject'));
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    const submitData = {
      name: formData.name,
      description: formData.description,
      start_date: formData.start_date || null,
      end_date: formData.end_date || null,
      location: formData.location || '',
      status: formData.status || null,
      type: formData.type || null,
      budget: formData.budget || null,
      customer: formData.customer || null,
      currency: formData.currency || null,
      tax_rate: formData.tax_rate || '21.00',
      version: formData.version || '1.0',
      locked: formData.locked,
      processed_by: formData.processed_by || null,
      discount: formData.discount ?? 0,
      billed_days: formData.billed_days ?? 1
    };
    saveMutation.mutate(submitData);
  };
  const saving = saveMutation.isPending;

  if (loading) {
    return (
      <Container className="mt-4">
        <CenteredLoader />
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Row className="mb-4">
        <Col>
          <h1>{t('projects.editProjectTitle', { name: formData.name })}</h1>
        </Col>
        <Col className="text-end">
          <Button
            variant="outline-secondary"
            className="me-2"
            onClick={() => navigate(`/projects/detail/${projectId}`)}
          >
            {t('projects.backToProject')}
          </Button>
          <Button
            variant="outline-primary"
            onClick={() => navigate('/projects')}
          >
            {t('projects.allProjects')}
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <Card>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            {formData.locked && (
              <Alert variant="warning">
                {t('projects.projectLocked')}
              </Alert>
            )}

            <Row className="mb-3">
              <Col>
                <Form.Check
                  type="checkbox"
                  id="project-locked"
                  name="locked"
                  label={t('projects.lockProject')}
                  checked={formData.locked}
                  onChange={handleChange}
                />
              </Col>
            </Row>

            <fieldset disabled={formData.locked}>
              {/* Row 1: Name + Customer */}
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('projects.projectName')} *</Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <div className="d-flex align-items-center justify-content-between mb-1">
                      <Form.Label className="mb-0">{t('projects.customer')}</Form.Label>
                      {selectedCustomerOption?.value && (
                        <a
                          href={`/customers/customer/${selectedCustomerOption.value}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-secondary"
                          title={t('common.openDetail')}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 6h-6a2 2 0 0 0 -2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-6" /><path d="M11 13l9 -9" /><path d="M15 4h5v5" /></svg>
                        </a>
                      )}
                    </div>
                    <AsyncCreatableSelect
                      name="customer"
                      value={selectedCustomerOption}
                      onChange={(option) => {
                        setSelectedCustomerOption(option);
                        setFormData(prev => ({ ...prev, customer: option?.value || '' }));
                      }}
                      loadOptions={loadCustomerOptions}
                      defaultOptions
                      cacheOptions
                      onCreateOption={handleCreateCustomer}
                      isClearable
                      isDisabled={formData.locked || creatingCustomer}
                      isLoading={creatingCustomer}
                      placeholder={t('projects.selectCustomer')}
                      formatCreateLabel={(inputValue) => `${t('common.create')}: "${inputValue}"`}
                      classNamePrefix="react-select"
                      className="react-select-container"
                      theme={selectTheme}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Row 2: Location + Status */}
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('projects.location')}</Form.Label>
                    <Form.Control
                      type="text"
                      name="location"
                      value={formData.location || ''}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('projects.status')} *</Form.Label>
                    <Select
                      name="status"
                      value={statusOptions.find(option => option.value === formData.status) || null}
                      onChange={handleSelectChange('status')}
                      options={statusOptions}
                      isClearable
                      placeholder={t('projects.selectStatus')}
                      isDisabled={formData.locked}
                      onInputChange={(inputValue) => {
                        if (inputValue) {
                          debouncedStatusSearch(inputValue);
                        } else {
                          // Reset to default options if input is cleared
                          setStatusOptions(projectStatuses.map(status => ({ value: status.id, label: status.name })));
                        }
                      }}
                      classNamePrefix="react-select"
                      className="react-select-container"
                      theme={selectTheme}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Row 3: Start date + End date + Processed by */}
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('projects.startDate')}</Form.Label>
                    <Form.Control
                      type="date"
                      name="start_date"
                      value={formData.start_date || ''}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('projects.endDate')}</Form.Label>
                    <Form.Control
                      type="date"
                      name="end_date"
                      value={formData.end_date || ''}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('projects.processedBy')}</Form.Label>
                    <Select
                      name="processed_by"
                      value={crewMemberOptions.find(o => o.value === formData.processed_by) || null}
                      onChange={handleSelectChange('processed_by')}
                      options={crewMemberOptions}
                      isClearable
                      placeholder={t('projects.selectProcessedBy')}
                      isDisabled={formData.locked}
                      classNamePrefix="react-select"
                      className="react-select-container"
                      theme={selectTheme}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Row 4: Type + Budget */}
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('projects.type')}</Form.Label>
                    <Select
                      name="type"
                      value={typeOptions.find(option => option.value === formData.type) || null}
                      onChange={handleSelectChange('type')}
                      options={typeOptions}
                      isClearable
                      placeholder={t('projects.selectType')}
                      isDisabled={formData.locked}
                      onInputChange={(inputValue) => {
                        if (inputValue) {
                          debouncedTypeSearch(inputValue);
                        } else {
                          // Reset to default options if input is cleared
                          setTypeOptions(projectTypes.map(type => ({ value: type.id, label: type.name })));
                        }
                      }}
                      classNamePrefix="react-select"
                      className="react-select-container"
                      theme={selectTheme}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('projects.budget')}</Form.Label>
                    <Form.Control
                      type="number"
                      name="budget"
                      value={formData.budget || ''}
                      onChange={handleChange}
                      step="any"
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Row 5: Currency + Tax rate */}
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('projects.currency')}</Form.Label>
                    <Select
                      name="currency"
                      value={currencyOptions.find(option => option.value === formData.currency) || null}
                      onChange={handleSelectChange('currency')}
                      options={currencyOptions}
                      isClearable
                      placeholder={t('projects.selectCurrency')}
                      isDisabled={formData.locked}
                      classNamePrefix="react-select"
                      className="react-select-container"
                      theme={selectTheme}
                      noOptionsMessage={() => t('projects.noCurrencies')}
                    />
                    <Form.Text className="text-muted">
                      {t('projects.currencyHelp')}
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('projects.taxRate')}</Form.Label>
                    <Form.Control
                      type="number"
                      name="tax_rate"
                      value={formData.tax_rate || '21.00'}
                      onChange={handleChange}
                      step="any"
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Row 6: Version */}
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('projects.version')}</Form.Label>
                    <Form.Control
                      type="text"
                      name="version"
                      value={formData.version || '1.0'}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Row 7: Description */}
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('projects.description')}</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="description"
                      value={formData.description || ''}
                      onChange={handleChange}
                      rows={4}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </fieldset>

            <div className="d-flex justify-content-end mt-4">
              <Button
                variant="secondary"
                className="me-2"
                onClick={() => navigate(`/projects/detail/${projectId}`)}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={saving || formData.locked}
              >
                {saving ? (
                  <>
                    <InlineLoader className="me-2" />
                    {t('common.saving')}
                  </>
                ) : t('projects.saveChanges')}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ProjectEdit;
